<!-- 
Guided Tour component

This component is used to display the guided tour.
It uses the intro.js library to display the guided tour.

Props:
- tourType: string
  - Type of guided tour to be displayed. Currently, only 'teacher-dashboard' is supported.
- tourOptions: object

-->

<template>
  <!-- Screen reader live region (exists only while tour is mounted) -->
  <div
    id="guided-tour-announcer"
    class="sr-only"
    aria-live="assertive"
    aria-atomic="true"
  ></div>
</template>

<script setup lang="ts">
/**
 * Imports
 */
import introJs from 'intro.js'; // intro.js library is used to display the guided tour

/**
 * PROPERTIES / PROPS0
 * (These are variables that are passed to the component from a parent component)
 */
const props = defineProps<{
  options: {
    disableScroll?: boolean;
    tooltipClass?: string;
    scrollTopForFloatingTooltip?: boolean;
    autoPosition?: boolean;
    helperElementPadding?: number;
  };
  steps: GuidedTourStep[];
}>();

/**
 * EMITS / OUTPUTS
 * (events emitted by the component, handled by parents)
 */
const emit = defineEmits<{
  // Emits the event when the guided tour is closed
  'guided-tour-exit': [];
}>();

/**
 * Interface for defining the structure of guided tour steps
 */
export interface GuidedTourStep {
  element?: string; // The element's selector string to highlight during the step
  descHeading: string; // The heading of the step description
  desc: string; // The description of the step
  announcement?: string; // The announcement text for screen readers
  image?: {
    src: string; // The source URL of the image for the step
    alt: string; // The alt text for the image
  };
  position?:
    | 'bottom-right-aligned'
    | 'bottom-left-aligned'
    | 'bottom-middle-aligned'; // The position of the tooltip for the step
}

const SCREEN_READER_DELAY_MS = 150;
/**
Announce message for screen readers using ARIA live region
*/
const announceForScreenReader = (message: string) => {
  const announcer = document.getElementById('guided-tour-announcer');
  if (!announcer) return;

  // Clear first so SR re-reads even same text
  announcer.textContent = '';

  setTimeout(() => {
    announcer.textContent = message;
  }, SCREEN_READER_DELAY_MS);
};

/**
 * Start Guilded Tour function
 */
const startGuidedTour = () => {
  /**
   * Check if the environment is development or production
   * and set the image base path accordingly
   */
  const isDevEnv = useRuntimeConfig().public.appEnv == 'local';
  let closeImagePath;
  if (isDevEnv) {
    closeImagePath = '/_nuxt/assets/img/close-guided-tour.svg';
  } else {
    closeImagePath =
      useRuntimeConfig().public.cdnURL + '/_nuxt/close-guided-tour.svg';
  }

  //observer for skip button focus
  let skipButtonObserver: MutationObserver | null = null;
  //generate steps
  const guidedTourSteps = generateSteps(props.steps);
  //initialize guidedTour
  const guidedTour = introJs();
  // Track last announced step to prevent duplicates (for screen reader fix)
  let lastAnnouncedStep = -1;

  if (props.options.disableScroll) document.body.style.overflowY = 'hidden';

  /**
   * Options for guidedTour
   */
  const guidedTourOptions = {
    showBullets: false,
    showStepNumbers: true,
    hidePrev: true,
    tooltipPosition: 'bottom-middle-aligned',
    tooltipClass: props.options.tooltipClass
      ? `walkthrough-tooltip ${props.options.tooltipClass}`
      : 'walkthrough-tooltip',
    highlightClass: 'walkthrough-helperLayer',
    exitOnOverlayClick: false,

    disableInteraction: true,
    overlayOpacity: 0.2,
    skipLabel: `<img src="${closeImagePath}" alt="Close Guided Tour"></img>`,
    scrollToElement: true,
    keyboardNavigation: true,
    autoPosition:
      props.options.autoPosition !== undefined
        ? props.options.autoPosition
        : true,
    steps: guidedTourSteps,
    helperElementPadding: props.options.helperElementPadding
      ? props.options.helperElementPadding
      : 10
  };

  // set waklkthrough options
  guidedTour.setOptions(guidedTourOptions);

  // focus on skip button after each step
  guidedTour.onafterchange(() => {
    // Stop previous observer before starting a new one
    stopCloseButtonFocusObserver();
    // Focus the skip button immediately
    setTimeout(focusSkipButton, 50);
    // Start observer for future updates
    startCloseButtonFocusObserver();

    // Screen reader announcement
    const stepIndex = guidedTour._currentStep;

    // Prevent duplicate announcements for the same step
    if (stepIndex === lastAnnouncedStep) {
      return;
    }
    lastAnnouncedStep = stepIndex;

    const currentStep = props.steps[stepIndex];

    if (currentStep) {
      let textToAnnounce =
        currentStep.announcement?.trim() ||
        currentStep.descHeading?.trim() ||
        currentStep.desc;

      announceForScreenReader(`Step ${stepIndex + 1}. ${textToAnnounce}`);
    }
  });

  /**
   * Focus on the skip/close button and make it keyboard accessible
   */
  const focusSkipButton = () => {
    const skipBtn = document.querySelector(
      '.introjs-skipbutton'
    ) as HTMLElement | null;

    if (skipBtn) {
      skipBtn.setAttribute('tabindex', '0');
      skipBtn.focus();
    }
  };

  const startCloseButtonFocusObserver = () => {
    if (skipButtonObserver) return;

    // Only observe the intro.js tooltip container, not the entire body
    const tooltipContainer = document.querySelector('.introjs-tooltipReferenceLayer');
    if (!tooltipContainer) {
      // If tooltip container doesn't exist yet, try focusing the button directly
      setTimeout(focusSkipButton, 100);
      return;
    }

    skipButtonObserver = new MutationObserver(focusSkipButton);
    skipButtonObserver.observe(tooltipContainer, {
      childList: true,
      subtree: true
    });
  };

  /**
  Stop observing DOM for skip button
  */
  const stopCloseButtonFocusObserver = () => {
    if (skipButtonObserver) {
      skipButtonObserver.disconnect();
      skipButtonObserver = null;
    }
  };

  // scroll to top for floating tooltip
  if (props.options.scrollTopForFloatingTooltip) {
    guidedTour.onchange((targetElement: Element) => {
      if (targetElement.classList.contains('introjsFloatingElement')) {
        //Added a setTimeout,
        // as introjs might be overriding the scroll position(in case no timeout is used)
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      }
    });
  }

  const elementsToDisable = Array.from(
    document.querySelectorAll('button, a, input')
  );

  // on exit of guidedTour
  guidedTour.onexit(() => {
    stopCloseButtonFocusObserver();

    removeTabIndex(elementsToDisable);
    // Restore screen reader access to page content
    document.querySelector('.wrapper')?.removeAttribute('aria-hidden');
    document.querySelector('.footer-root')?.removeAttribute('aria-hidden');
    if (props.options.disableScroll) document.body.style.overflowY = 'visible';

    // emit event to parent component
    emit('guided-tour-exit');
  });

  //add tabindex to all the elements.
  addTabIndex(elementsToDisable);
  startCloseButtonFocusObserver();
  guidedTour.start();
  
  // Hide page content from screen readers during tour (delay for lazy-loaded footer)
  setTimeout(() => {
    const announcer = document.getElementById('guided-tour-announcer');
    // Move announcer to body level so it's not affected by aria-hidden
    if (announcer && announcer.parentElement !== document.body) {
      document.body.appendChild(announcer);
    }
    // Hide main content and footer from screen readers
    document.querySelector('.wrapper')?.setAttribute('aria-hidden', 'true');
    document.querySelector('.footer-root')?.setAttribute('aria-hidden', 'true');
    // Announce first step if not already announced
    if (lastAnnouncedStep === -1) {
      const firstStep = props.steps[0];
      if (firstStep) {
        let textToAnnounce =
          firstStep.announcement?.trim() ||
          firstStep.descHeading?.trim() ||
          firstStep.desc;
        announceForScreenReader(`Step 1. ${textToAnnounce}`);
        lastAnnouncedStep = 0;
      }
    }
  }, 300);
  
};

/**
 * Function to add tabindex to all the elements
 */
const addTabIndex = (elements: Element[]) => {
  elements.forEach((element) => {
    element.setAttribute('tabindex', '-1');
  });
};

/**
 * Function to remove tabindex from all the elements
 */
const removeTabIndex = (elements: Element[]) => {
  elements.forEach((element) => {
    element.removeAttribute('tabindex');
  });
};

/**
 * Function to generate steps for introjs
 * @param steps : steps array
 * @returns Array of objects that introjs supports
 * The method will generate divs based on steps array (image div/description container etc)
 */
const generateSteps = (steps: GuidedTourStep[]) => {
  return steps?.map((step) => {
    let imageContainer = '';
    if (step.image && step.image.src) {
      const imageSrc = step.image.src;
      const imageAlt = step.image.alt ? step.image.alt : '';
      imageContainer = `<div class="image-container"><img src="${imageSrc}" alt="${imageAlt}"></div>`;
    }
    const intro = `
            <div class="intro-top">
                <div class="description-container">
                    <p class="description-heading">${step.descHeading}</p>
                    <p class="description">${step.desc}</p>
                </div>
            ${imageContainer}
            </div>`;
    return { intro, position: step.position, element: step.element };
  });
};

/**
 * Start the guidedTour
 */
startGuidedTour();
</script>

<style lang="scss">
// Styles used for guided tour.
@import '../../../assets/scss/introjs.scss';
@import '../../../assets/scss/guidedtour.scss';
</style>
